import type { Wallet } from "@/app/types/wallet";
import { WalletProviderType } from "../services/enum/wallet-provider-type";
import { WalletFactory } from "./walletFactory";
import { clearWalletData } from "./walletUtils";

export class WalletService {
  private wallet: Wallet | null = null;

  private normalizeWalletType(
    walletProviderType?: string
  ): WalletProviderType | null {
    switch (walletProviderType) {
      case WalletProviderType.METAMASK:
      case "io.metamask":
        return WalletProviderType.METAMASK;
      case WalletProviderType.COINBASE:
      case "com.coinbase.wallet":
        return WalletProviderType.COINBASE;
      case WalletProviderType.WALLET_CONNECT:
      case "walletConnect":
        return WalletProviderType.WALLET_CONNECT;
      case WalletProviderType.WEB3AUTH:
      case WalletProviderType.WEB3AUTH_WEB2:
      case WalletProviderType.WEB3AUTH_WEB3:
      case "inApp":
      case "embedded":
      case "com.thirdweb":
        return WalletProviderType.WEB3AUTH;
      default:
        return null;
    }
  }

  async checkIfConnected(
    walletProviderType: WalletProviderType
  ): Promise<
    string | { accounts: string[]; providerType: WalletProviderType }
  > {
    const normalizedType = this.normalizeWalletType(walletProviderType);
    if (!normalizedType) {
      return "";
    }

    try {
      this.wallet = new WalletFactory().getWallet(normalizedType);
      return this.wallet ? await this.wallet.isConnected(normalizedType) : "";
    } catch {
      return "";
    }
  }

  async connectWallet(walletProviderType: WalletProviderType) {
    const normalizedType = this.normalizeWalletType(walletProviderType);
    if (!normalizedType) {
      return "";
    }

    if (!this.wallet || !(await this.checkIfConnected(normalizedType))) {
      this.wallet = new WalletFactory().getWallet(normalizedType);
      const response = await this.wallet?.connect();
      //   const walletId = await this.wallet?.getAddress();
      return response;
    } else {
      this.wallet = new WalletFactory().getWallet(normalizedType);
      return this.wallet?.connect();
    }
  }

  async signTransaction(
    walletId: string,
    challengeMessage: string,
    walletProviderType: WalletProviderType
  ): Promise<string> {
    const normalizedType = this.normalizeWalletType(walletProviderType);
    if (!normalizedType) {
      return Promise.reject("Unknown wallet type");
    }

    if (!this.wallet) {
      this.wallet = new WalletFactory().getWallet(normalizedType);
    }
    return await this.wallet?.signTransaction(walletId, challengeMessage)!;
  }

  async disconnectWallet(walletProviderType: WalletProviderType) {
    const normalizedType = this.normalizeWalletType(walletProviderType);
    if (!normalizedType) {
      clearWalletData();
      this.wallet = null;
      return;
    }

    if (!this.wallet) {
      this.wallet = new WalletFactory().getWallet(normalizedType);
    }
    await this.wallet?.disconnect();
    clearWalletData();
    this.wallet = null;
  }

  getConnectedWallet() {
    return this.wallet?.getConnectedWallet();
  }
}
